import React, { Fragment, Suspense, lazy, useEffect, useState } from "react";
import {
  ThemeProvider,
  StyledEngineProvider,
  CssBaseline,
  createTheme,
  responsiveFontSizes,
} from "@mui/material";
import { BrowserRouter, Route, Switch, useLocation } from "react-router-dom";
import { ParallaxProvider } from "react-scroll-parallax";
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { theme, darkPalette, lightPalette } from "./theme";
import GlobalStyles from "./GlobalStyles";
import Pace from "./shared/components/Pace";
import AIChatbot from "./components/AIChatbot";
import AdminChatInterface from "./components/AdminChatInterface";
import { Provider } from "react-redux";
import store from "./store/store";
import jwt_decode from "jwt-decode";
import setAuthToken from "./utils/setAuthToken";
import {

  setCurrentUser,
} from "./store/features/login/login-slice";

// Mobile Detection Components
import useMobileDetection from "./hooks/useMobileDetection";
import MobileDetectionModal from "./shared/components/MobileDetectionModal";
import MobileNoticeBanner from "./shared/components/MobileNoticeBanner";

const LoggedInComponent = lazy(() => import("./logged_in/components/Main"));
const LoggedOutComponent = lazy(() => import("./logged_out/components/Main"));
const AuthComponent = lazy(() => import("./logged_out/components/auth/Main"));
const WaitlistComponent = lazy(() =>
  import("./logged_out/components/waitlist/Waitlist")
);

// Check for token to keep user logged in
if (localStorage.jwtToken) {
  // Set auth token header auth
  const token = localStorage.jwtToken;
  setAuthToken(token);
  // Decode token and get user info and exp
  const decoded = jwt_decode(token);
  // Set user and isAuthenticated
  console.log("user already logged in on start");
  store.dispatch(setCurrentUser(decoded));
  // store.dispatch(updateLoginState(true));
  // //store.dispatch(setUserID(decoded))
  // // Check for expired token
  // const currentTime = Date.now() / 1000; // to get in milliseconds
  // if (decoded.exp < currentTime) {
  //   // Logout user
  //   //store.dispatch(logoutUser());
  //   // Redirect to login
  //   window.location.href = "./login";
  // }
}

// AI Chatbot Container - conditionally renders based on route
function AIChatbotContainer() {
  const location = useLocation();
  const isAuthRoute = location.pathname.startsWith('/auth');
  const isWaitlistRoute = location.pathname.startsWith('/waitlist');
  const isAdminRoute = location.pathname.startsWith('/admin');
  
  // Don't show chatbot on auth, waitlist, or admin pages
  if (isAuthRoute || isWaitlistRoute || isAdminRoute) {
    return null;
  }
  
  return <AIChatbot />;
}

// Mobile Detection Wrapper Component
function MobileDetectionWrapper({ children, currentTheme }) {
  const { isMobile, deviceInfo } = useMobileDetection();
  const location = useLocation();
  
  const [showLoggedInModal, setShowLoggedInModal] = useState(false);
  
  // Determine if we're on logged-in routes
  const isLoggedInRoute = location.pathname.startsWith('/c');
  const isAuthRoute = location.pathname.startsWith('/auth');
  const isWaitlistRoute = location.pathname.startsWith('/waitlist');
  
  useEffect(() => {
    if (isMobile) {
      if (isLoggedInRoute) {
        // Show blocking modal for logged-in routes
        setShowLoggedInModal(true);
      }
    }
    

  }, [isMobile, isLoggedInRoute, deviceInfo, isAuthRoute, isWaitlistRoute, location.pathname]);
  
  const handleContinueAnyway = () => {
    setShowLoggedInModal(false);
  };
  
  return (
    <>
      {/* Banner for logged-out pages (non-blocking) */}
      {isMobile && !isLoggedInRoute && !isWaitlistRoute && (
        <MobileNoticeBanner 
          deviceInfo={deviceInfo}
        />
      )}
      
      {/* Main content */}
      {children}
      
      {/* Blocking modal for logged-in routes */}
      <MobileDetectionModal
        open={showLoggedInModal}
        onClose={() => setShowLoggedInModal(false)}
        onContinue={handleContinueAnyway}
        deviceInfo={deviceInfo}
        blocking={deviceInfo?.deviceType === 'mobile'} // Tablets can continue, phones cannot
      />
    </>
  );
}

function App() {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedMode = localStorage.getItem("themeMode");
    return savedMode ? savedMode === "dark" : true;
  });
  const toggleDarkMode = () => {
    setIsDarkMode(!isDarkMode);
    localStorage.setItem("themeMode", isDarkMode ? "light" : "dark");
  };

  useEffect(() => {
    localStorage.setItem("mode", "company");
  }, []);

  const darkTheme = responsiveFontSizes(
    createTheme({ ...theme, palette: darkPalette })
  );
  const lightTheme = responsiveFontSizes(
    createTheme({ ...theme, palette: lightPalette })
  );
  const currentTheme = isDarkMode ? darkTheme : lightTheme;

  return (
    <Provider store={store}>
      <ParallaxProvider>
        <BrowserRouter>
          <StyledEngineProvider injectFirst>
            <ThemeProvider theme={currentTheme}>
              <CssBaseline />
              <GlobalStyles />
              <Pace color={theme.palette.primary.light} />
              
              <MobileDetectionWrapper currentTheme={currentTheme}>
                <Suspense fallback={<Fragment />}>
                  <Switch>
                    <Route path="/admin/chat">
                      <AdminChatInterface />
                    </Route>
                    <Route path="/c">
                      <LoggedInComponent toggleDarkMode={toggleDarkMode} />
                    </Route>
                    <Route path="/auth">
                      <AuthComponent />
                    </Route>
                    <Route path="/waitlist">
                      <WaitlistComponent />
                    </Route>
                    <Route>
                      <LoggedOutComponent
                        themeMode={isDarkMode}
                        toggleDarkMode={toggleDarkMode}
                      />
                    </Route>
                  </Switch>
                </Suspense>
              </MobileDetectionWrapper>
              
              {/* AI Chatbot - Show on all pages except auth and waitlist */}
              <AIChatbotContainer />
              
              <ToastContainer
                position="top-right"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme={isDarkMode ? "dark" : "light"}
              />
            </ThemeProvider>
          </StyledEngineProvider>
        </BrowserRouter>
      </ParallaxProvider>
    </Provider>
  );
}

export default App;
